// frontend/src/pages/settings/users-roles/AreaSection.tsx
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CAP_BY_KEY, KEY_LEVEL_ICON, isKeyLevel, type CatalogCapability } from '../../../lib/rbac';
import type { AreaFlags } from './types';

interface CapRowProps {
  c: CatalogCapability;
  /** Valor base (grant explícito ?? heredado del área): es lo que la tilde muestra y lo que el clic invierte. */
  checked: boolean;
  /** false = le falta el prerequisito; atenúa la fila y muestra la nota, pero NO destilda. */
  reqOk: boolean;
  explicit: boolean;
  readOnly: boolean;
  onToggle: (cap: CatalogCapability) => void;
}

/** Componente de nivel superior: definido fuera de AreaSection para que las tildes no se re-monten en cada render. */
function CapRow({ c, checked, reqOk, explicit, readOnly, onToggle }: CapRowProps) {
  const key = isKeyLevel(c.level);
  return (
    <label
      className={`flex items-start gap-2 px-3 py-1.5 text-xs rounded cursor-pointer ${readOnly ? 'cursor-default' : 'hover:bg-[var(--color-surface-hover)]'} ${!reqOk ? 'opacity-50' : ''}`}
      title={c.description || c.label}
    >
      <input type="checkbox" className="mt-0.5" checked={checked} disabled={readOnly} onChange={() => onToggle(c)} />
      <span className={key ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--color-text)]'}>
        {key && <span className="mr-1">{KEY_LEVEL_ICON[c.level]}</span>}
        {c.label}
        {explicit && !key && <span className="ml-1 text-[10px] text-[var(--color-text-muted)]">(excepción)</span>}
        {!reqOk && (
          <span className="ml-1 text-[10px] text-[var(--color-text-muted)]">
            — requiere «{CAP_BY_KEY.get(c.requires ?? '')?.label}»
          </span>
        )}
        {c.description && <div className="text-[10px] text-[var(--color-text-muted)]">{c.description}</div>}
      </span>
    </label>
  );
}

interface Props {
  areaKey: string;
  label: string;
  caps: CatalogCapability[];
  flags: AreaFlags;
  grants: Record<string, boolean>;
  /** Valor base de una capacidad: lo que se pinta en la tilde. */
  base: (cap: string) => boolean;
  /** Valor efectivo (base + prerequisitos cumplidos): alimenta el contador y la nota "requiere". */
  effective: (cap: string) => boolean;
  readOnly: boolean;
  onToggleArea: (level: 'view' | 'operate', value: boolean) => void;
  onToggleCap: (cap: CatalogCapability) => void;
}

export function AreaSection({ areaKey, label, caps, flags, grants, base, effective, readOnly, onToggleArea, onToggleCap }: Props) {
  const [open, setOpen] = useState(false);
  const isGlobal = areaKey === 'global';
  const hasOperate = caps.some((c) => c.level === 'operate');
  const onCount = caps.filter((c) => effective(c.key)).length;
  const tabs = caps.filter((c) => c.key.includes('.tab.'));
  const others = caps.filter((c) => !c.key.includes('.tab.'));

  const row = (c: CatalogCapability) => (
    <CapRow
      key={c.key}
      c={c}
      checked={base(c.key)}
      reqOk={!c.requires || effective(c.requires)}
      explicit={c.key in grants}
      readOnly={readOnly}
      onToggle={onToggleCap}
    />
  );

  return (
    <div className="border border-[var(--color-border)] rounded-lg">
      <div className="flex items-center gap-3 px-3 py-2 bg-[var(--color-bg)] rounded-t-lg">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-sm font-medium text-[var(--color-text)] flex-1 text-left">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {label}
          <span className="ml-2 text-[10px] font-normal text-[var(--color-text-muted)]">{onCount}/{caps.length}</span>
        </button>
        {!isGlobal && (
          <>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={flags.view_all} disabled={readOnly} onChange={(e) => onToggleArea('view', e.target.checked)} /> Ver
            </label>
            {hasOperate && (
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={flags.operate_all} disabled={readOnly} onChange={(e) => onToggleArea('operate', e.target.checked)} /> Operar
              </label>
            )}
          </>
        )}
      </div>
      {open && (
        <div className="p-2 space-y-2">
          {tabs.length > 0 && (
            <div>
              <div className="px-3 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">Pestañas visibles</div>
              <div className="grid sm:grid-cols-2">{tabs.map(row)}</div>
            </div>
          )}
          <div>
            {tabs.length > 0 && <div className="px-3 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">Funciones</div>}
            {others.map(row)}
          </div>
        </div>
      )}
    </div>
  );
}
